import { endOfWeek, startOfWeek } from "../utils/dateUtils";

export const DEFAULT_PENDING_INVITATION_BLOCK_HEADLINE = Object.freeze({
  blockCurrentName: "-",
  blockCurrentWeek: 0,
  blockWeeks: 0,
  blockCurrentWeekPrepared: true,
});

const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

const cloneDefaultHeadline = () => ({
  blockCurrentName: DEFAULT_PENDING_INVITATION_BLOCK_HEADLINE.blockCurrentName,
  blockCurrentWeek: DEFAULT_PENDING_INVITATION_BLOCK_HEADLINE.blockCurrentWeek,
  blockWeeks: DEFAULT_PENDING_INVITATION_BLOCK_HEADLINE.blockWeeks,
  blockCurrentWeekPrepared:
    DEFAULT_PENDING_INVITATION_BLOCK_HEADLINE.blockCurrentWeekPrepared,
});

const toDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === "function") {
    const parsedDate = value.toDate();
    return parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime())
      ? parsedDate
      : null;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return parsedDate;
};

const hasNumericWeek = (value) => typeof value === "number" && value > 0;

const normalizeWorkouts = (value) => (Array.isArray(value) ? value : []);

const resolveBlocksWithWorkoutDates = (blocksData = []) =>
  blocksData.map((blockData) => {
    const workouts = normalizeWorkouts(blockData?.workouts);
    const workoutDates = workouts.map((workout) => toDate(workout?.date)).filter(Boolean);
    return {
      blockData,
      workouts,
      workoutDates,
    };
  });

const resolveTargetBlock = ({
  resolvedBlocks,
  startOfThisWeek,
  endOfThisWeek,
}) => {
  let targetBlock = resolvedBlocks.find(({ workoutDates }) =>
    workoutDates.some((date) => date >= startOfThisWeek && date <= endOfThisWeek)
  );

  if (targetBlock) return targetBlock;

  const blocksWithWorkoutsBeforeThisWeek = resolvedBlocks.filter(({ workoutDates }) =>
    workoutDates.some((date) => date < startOfThisWeek)
  );

  if (blocksWithWorkoutsBeforeThisWeek.length > 0) {
    let mostRecentWorkout = null;
    let mostRecentBlock = null;

    blocksWithWorkoutsBeforeThisWeek.forEach((block) => {
      block.workoutDates.forEach((date) => {
        if (date < startOfThisWeek && (!mostRecentWorkout || date > mostRecentWorkout)) {
          mostRecentWorkout = date;
          mostRecentBlock = block;
        }
      });
    });

    if (mostRecentBlock) {
      const workoutsWithDates = normalizeWorkouts(mostRecentBlock.blockData?.workouts).filter(
        (workout) => toDate(workout?.date) && hasNumericWeek(workout?.week)
      );

      const workoutWithMaxWeek = workoutsWithDates.reduce((maxWorkout, currentWorkout) => {
        if (!maxWorkout || currentWorkout.week > maxWorkout.week) {
          return currentWorkout;
        }
        return maxWorkout;
      }, null);

      if (workoutWithMaxWeek) {
        const totalWeeks = Number(mostRecentBlock.blockData?.total_weeks) || 0;
        const pendingWeeks = totalWeeks - workoutWithMaxWeek.week;
        const lastWorkoutDate = toDate(workoutWithMaxWeek.date);

        if (lastWorkoutDate) {
          const estimatedEndDate = new Date(lastWorkoutDate);
          estimatedEndDate.setDate(estimatedEndDate.getDate() + pendingWeeks * 7);

          if (estimatedEndDate >= startOfThisWeek) {
            targetBlock = mostRecentBlock;
          }
        }
      }
    }
  }

  if (targetBlock) return targetBlock;

  for (const block of resolvedBlocks) {
    const calendarStartDate = toDate(block.blockData?.calendar_start_date);
    const hasWorkoutsWithDates = block.workoutDates.length > 0;
    if (!calendarStartDate || hasWorkoutsWithDates) continue;

    const calendarStartWeek = startOfWeek(calendarStartDate);
    if (calendarStartWeek > startOfThisWeek) continue;

    const totalWeeks = Number(block.blockData?.total_weeks) || 0;
    const estimatedBlockEndDate = new Date(calendarStartDate);
    estimatedBlockEndDate.setDate(
      estimatedBlockEndDate.getDate() + (Math.max(totalWeeks, 1) - 1) * 7
    );

    if (estimatedBlockEndDate >= startOfThisWeek) {
      targetBlock = block;
      break;
    }
  }

  return targetBlock ?? null;
};

const resolveCurrentWeek = ({
  workouts,
  blockTotalWeeks,
  startOfThisWeek,
  endOfThisWeek,
  calendarStartDate,
}) => {
  const thisWeekWorkouts = workouts.filter((workout) => {
    const workoutDate = toDate(workout?.date);
    return workoutDate && workoutDate >= startOfThisWeek && workoutDate <= endOfThisWeek;
  });

  if (thisWeekWorkouts.length > 0) {
    return Math.max(...thisWeekWorkouts.map((workout) => workout.week));
  }

  const pastWorkouts = workouts.filter((workout) => {
    const workoutDate = toDate(workout?.date);
    return workoutDate && workoutDate < startOfThisWeek;
  });

  if (pastWorkouts.length === 0) {
    const futureWorkouts = workouts.filter((workout) => {
      const workoutDate = toDate(workout?.date);
      return workoutDate && workoutDate > endOfThisWeek;
    });

    if (futureWorkouts.length > 0) {
      return Math.min(...futureWorkouts.map((workout) => workout.week));
    }

    if (calendarStartDate) {
      const calendarStartWeek = startOfWeek(calendarStartDate);
      const weeksDiff = Math.floor((startOfThisWeek - calendarStartWeek) / WEEK_IN_MS);

      return Math.min(Math.max(1, 1 + weeksDiff), blockTotalWeeks);
    }

    const allWeeks = workouts
      .filter((workout) => hasNumericWeek(workout?.week))
      .map((workout) => workout.week);
    return allWeeks.length > 0 ? Math.max(...allWeeks) : 1;
  }

  const mostRecentPastWorkout = pastWorkouts.reduce((latest, workout) => {
    const workoutDate = toDate(workout?.date);
    if (!workoutDate) return latest;

    return !latest || workoutDate > latest.date
      ? { date: workoutDate, week: workout.week }
      : latest;
  }, null);

  if (!mostRecentPastWorkout) {
    return 1;
  }

  const highestPastWeek = Math.max(...pastWorkouts.map((workout) => workout.week));
  const mostRecentWeekStart = startOfWeek(mostRecentPastWorkout.date);
  const weeksDiff = Math.floor((startOfThisWeek - mostRecentWeekStart) / WEEK_IN_MS);
  const estimatedWeek = highestPastWeek + weeksDiff;
  return Math.min(estimatedWeek, blockTotalWeeks);
};

export const resolvePendingInvitationBlockHeadline = (
  blocksData = [],
  referenceDate = new Date()
) => {
  if (!Array.isArray(blocksData) || blocksData.length === 0) {
    return cloneDefaultHeadline();
  }

  const resolvedBlocks = resolveBlocksWithWorkoutDates(blocksData);
  const startOfThisWeek = startOfWeek(referenceDate);
  const endOfThisWeek = endOfWeek(referenceDate);

  const targetBlock = resolveTargetBlock({
    resolvedBlocks,
    startOfThisWeek,
    endOfThisWeek,
  });

  if (!targetBlock) {
    return cloneDefaultHeadline();
  }

  const workouts = normalizeWorkouts(targetBlock.blockData?.workouts);
  const blockTotalWeeks = Number(targetBlock.blockData?.total_weeks) || 0;
  const blockCurrentNameRaw = targetBlock.blockData?.name;
  const blockCurrentName =
    typeof blockCurrentNameRaw === "string" && blockCurrentNameRaw.trim() !== ""
      ? blockCurrentNameRaw
      : DEFAULT_PENDING_INVITATION_BLOCK_HEADLINE.blockCurrentName;

  const calendarStartDate = toDate(targetBlock.blockData?.calendar_start_date);
  const blockCurrentWeek = resolveCurrentWeek({
    workouts,
    blockTotalWeeks,
    startOfThisWeek,
    endOfThisWeek,
    calendarStartDate,
  });

  const maxCreatedWeek = Math.max(
    ...workouts.map((workout) => (hasNumericWeek(workout?.week) ? workout.week : 0))
  );
  const blockCurrentWeekPrepared = blockCurrentWeek <= maxCreatedWeek;

  return {
    blockCurrentName,
    blockCurrentWeek,
    blockWeeks: blockTotalWeeks,
    blockCurrentWeekPrepared,
  };
};
